"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useState, useCallback, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import type { CreateDropConfig } from "@/entities/IDrop";
import CreateDropStormParts from "./CreateDropStormParts";
import { AnimatePresence, motion } from "framer-motion";
import CreateDropContent from "./CreateDropContent";
import CreateCurationDropContent from "./CreateCurationDropContent";
import QuorumProposalDropModal from "./quorum/QuorumProposalDropModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ApiWave } from "@/generated/models/ApiWave";
import { QueryKey, ReactQueryWrapperContext } from "../react-query-wrapper/ReactQueryWrapper";
import { commonApiPost } from "@/services/api/common-api";
import type { ApiCreateDropRequest } from "@/generated/models/ApiCreateDropRequest";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import { AuthContext } from "../auth/Auth";
import { useKeyPressEvent } from "react-use";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { CurationComposerVariant, IdentityPickerPlacement } from "./dropComposer.types";
import { DropMode } from "./dropComposer.types";
import { ChatRestriction, type DropPrivileges } from "@/hooks/useDropPriviledges";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { ProcessIncomingDropType } from "@/contexts/wave/hooks/useWaveRealtimeUpdater";
import { useUnreadDividerOptional } from "@/contexts/wave/UnreadDividerContext";
import { useWave } from "@/hooks/useWave";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
import { getMentionedGroupsFromParts } from "@/helpers/waves/drop-group-mentions";
interface CreateDropProps {
  readonly activeDrop: ActiveDropState | null;
  readonly onCancelReplyQuote: () => void;
  readonly onDropAddedToQueue: () => void;
  readonly onAllDropsAdded?: (() => void) | undefined;
  readonly onExitFixedDropMode?: (() => void) | undefined;
  readonly wave: ApiWave;
  readonly dropId: string | null;
  readonly fixedDropMode: DropMode;
  readonly privileges: DropPrivileges;
  readonly curationComposerVariant?: CurationComposerVariant | undefined;
  readonly initialCurationUrl?: string | null | undefined;
  readonly onSubmitCurationUrl?: ((url: string) => void) | undefined;
  readonly canSubmitCurationUrl?: boolean | undefined;
  readonly curationUrlSubmitRestrictionMessage?: string | null | undefined;
  readonly externalAttachmentDrop?: {
    readonly token: number;
    readonly files: File[];
  } | null | undefined;
  readonly onExternalAttachmentDropConsumed?: (() => void) | undefined;
  readonly termsSignatureFlowEnabled?: boolean | undefined;
  readonly identityPickerPlacement?: IdentityPickerPlacement | undefined;
}
export interface DropMutationBody {
  readonly drop: ApiCreateDropRequest;
  readonly dropId: string | null;
  readonly onSuccess?: (() => void) | undefined;
  readonly onError?: ((error: unknown) => void) | undefined;
}
const ANIMATION_DURATION = 0.3;
interface SlowModeChatReservation {
  readonly id: number;
  readonly waveId: string;
  readonly cooldownMs: number;
}
interface QueuedDropMutationBody extends DropMutationBody {
  readonly slowModeChatReservation?: SlowModeChatReservation | undefined;
}
interface SlowModeChatWaveState {
  pendingReservationId: number | null;
  cooldownUntil: number | null;
  cooldownMs: number | null;
}
export default function CreateDrop(t0) {
  const $ = _c(139);
  const {
    activeDrop,
    onCancelReplyQuote,
    onDropAddedToQueue,
    onAllDropsAdded,
    onExitFixedDropMode,
    wave,
    dropId,
    fixedDropMode,
    privileges,
    curationComposerVariant: t1,
    initialCurationUrl: t2,
    onSubmitCurationUrl,
    canSubmitCurationUrl,
    curationUrlSubmitRestrictionMessage: t3,
    externalAttachmentDrop,
    onExternalAttachmentDropConsumed,
    termsSignatureFlowEnabled: t4,
    identityPickerPlacement: t5
  } = t0;
  const curationComposerVariant = t1 === undefined ? "default" : t1;
  const initialCurationUrlProp = t2 === undefined ? null : t2;
  const curationUrlSubmitRestrictionMessage = t3 === undefined ? null : t3;
  const termsSignatureFlowEnabled = t4 === undefined ? true : t4;
  const identityPickerPlacement = t5 === undefined ? "modal" : t5;
  const {
    setToast,
    connectedProfile
  } = useContext(AuthContext);
  const {
    waitAndInvalidateDrops
  } = useContext(ReactQueryWrapperContext);
  const queryClient = useQueryClient();
  const unreadDividerContext = useUnreadDividerOptional();
  let t6;
  if ($[0] !== onCancelReplyQuote) {
    t6 = () => onCancelReplyQuote();
    $[0] = onCancelReplyQuote;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  useKeyPressEvent("Escape", t6);
  const [isStormMode, setIsStormMode] = useState(false);
  const [drop, setDrop] = useState(null);
  const [dropModeOverride, setDropModeOverride] = useState(null);
  const [curationPrefillSeed, setCurationPrefillSeed] = useState(null);
  const [dismissedQuorumProposalScope, setDismissedQuorumProposalScope] = useState(null);
  const {
    processDropRemoved,
    processIncomingDrop
  } = useMyStream();
  const {
    isMemesWave,
    isCurationWave,
    isQuorumWave
  } = useWave(wave);
  const t7 = wave.participation.submission_strategy ?? null;
  let t8;
  if ($[2] !== isCurationWave || $[3] !== isMemesWave || $[4] !== isQuorumWave || $[5] !== t7) {
    t8 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      isQuorumWave,
      submissionStrategy: t7
    });
    $[2] = isCurationWave;
    $[3] = isMemesWave;
    $[4] = isQuorumWave;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const submissionExperience = t8;
  const canUseChatComposer = wave.chat.authenticated_user_eligible || privileges.chatRestriction === ChatRestriction.SLOW_MODE;
  let t9;
  if ($[7] !== activeDrop || $[8] !== canUseChatComposer || $[9] !== fixedDropMode || $[10] !== wave.participation.authenticated_user_eligible) {
    t9 = () => {
      if (fixedDropMode === DropMode.CHAT) {
        return false;
      }
      if (fixedDropMode === DropMode.PARTICIPATION) {
        return true;
      }
      if (canUseChatComposer) {
        return false;
      }
      if (wave.participation.authenticated_user_eligible) {
        return true;
      }
      if (activeDrop) {
        return false;
      }
      return false;
    };
    $[7] = activeDrop;
    $[8] = canUseChatComposer;
    $[9] = fixedDropMode;
    $[10] = wave.participation.authenticated_user_eligible;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const getDefaultIsDropMode = t9;
  const activeDropScope = activeDrop === null ? "none" : `${activeDrop.action}:${activeDrop.drop.id}:${activeDrop.partId}`;
  const modeScopeKey = `${wave.id}:${fixedDropMode}:${canUseChatComposer}:` + `${wave.participation.authenticated_user_eligible}:${activeDropScope}`;
  const modeScopeToken = modeScopeKey;
  const defaultIsDropMode = getDefaultIsDropMode();
  const isDropMode = dropModeOverride?.scopeKey === modeScopeToken ? dropModeOverride.value : defaultIsDropMode;
  const initialCurationUrl = curationPrefillSeed?.scopeKey === modeScopeToken ? curationPrefillSeed.url : initialCurationUrlProp;
  const isCurationDropMode = submissionExperience === WaveSubmissionExperience.CURATION_LEGACY && isDropMode;
  const isQuorumProposalDropMode = submissionExperience === WaveSubmissionExperience.QUORUM_PROPOSAL && isDropMode;
  const quorumProposalScopeKey = `${modeScopeToken}:${submissionExperience}`;
  const isQuorumProposalModalOpen = isQuorumProposalDropMode && dismissedQuorumProposalScope !== quorumProposalScopeKey;
  const canMentionAll = wave.wave.authenticated_user_eligible_for_admin === true;
  const canUseCurationUrlSubmit = fixedDropMode === DropMode.CHAT ? onSubmitCurationUrl !== undefined && canSubmitCurationUrl !== false : true;
  const curationUrlRestrictionMessage = fixedDropMode === DropMode.CHAT && onSubmitCurationUrl !== undefined && canSubmitCurationUrl === false ? curationUrlSubmitRestrictionMessage : null;
  let t10;
  if ($[12] !== canUseChatComposer || $[13] !== fixedDropMode || $[14] !== setToast || $[15] !== wave.participation.authenticated_user_eligible) {
    t10 = newIsDropMode => {
      if (fixedDropMode !== DropMode.BOTH) {
        return false;
      }
      if (newIsDropMode && !wave.participation.authenticated_user_eligible) {
        setToast({
          message: "You are not eligible to drop in this wave",
          type: "error"
        });
        return false;
      }
      if (!newIsDropMode && !canUseChatComposer) {
        setToast({
          message: "You are not eligible to chat in this wave",
          type: "error"
        });
        return false;
      }
      return true;
    };
    $[12] = canUseChatComposer;
    $[13] = fixedDropMode;
    $[14] = setToast;
    $[15] = wave.participation.authenticated_user_eligible;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const canSwitchDropMode = t10;
  let t11;
  if ($[17] !== canSwitchDropMode || $[18] !== fixedDropMode || $[19] !== modeScopeToken || $[20] !== onExitFixedDropMode) {
    t11 = newIsDropMode_0 => {
      if (!newIsDropMode_0 && fixedDropMode === DropMode.PARTICIPATION && onExitFixedDropMode) {
        setCurationPrefillSeed(null);
        setDismissedQuorumProposalScope(null);
        onExitFixedDropMode();
        return;
      }
      if (!canSwitchDropMode(newIsDropMode_0)) {
        return;
      }
      setCurationPrefillSeed(null);
      setDismissedQuorumProposalScope(null);
      setDropModeOverride({
        scopeKey: modeScopeToken,
        value: newIsDropMode_0
      });
    };
    $[17] = canSwitchDropMode;
    $[18] = fixedDropMode;
    $[19] = modeScopeToken;
    $[20] = onExitFixedDropMode;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const onDropModeChange = t11;
  let t12;
  if ($[22] !== canSubmitCurationUrl || $[23] !== canSwitchDropMode || $[24] !== fixedDropMode || $[25] !== modeScopeToken || $[26] !== onSubmitCurationUrl) {
    t12 = url => {
      if (fixedDropMode === DropMode.CHAT) {
        if (onSubmitCurationUrl && canSubmitCurationUrl !== false) {
          onSubmitCurationUrl(url);
        }
        return;
      }
      if (!canSwitchDropMode(true)) {
        return;
      }
      setCurationPrefillSeed({
        scopeKey: modeScopeToken,
        url
      });
      setDismissedQuorumProposalScope(null);
      setDropModeOverride({
        scopeKey: modeScopeToken,
        value: true
      });
    };
    $[22] = canSubmitCurationUrl;
    $[23] = canSwitchDropMode;
    $[24] = fixedDropMode;
    $[25] = modeScopeToken;
    $[26] = onSubmitCurationUrl;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const onSwitchToDropModeWithUrl = t12;
  let t13;
  if ($[28] !== canUseChatComposer || $[29] !== fixedDropMode || $[30] !== onDropModeChange || $[31] !== onExitFixedDropMode || $[32] !== quorumProposalScopeKey) {
    t13 = () => {
      if (fixedDropMode === DropMode.BOTH && canUseChatComposer) {
        onDropModeChange(false);
        return;
      }
      if (fixedDropMode === DropMode.PARTICIPATION && onExitFixedDropMode !== undefined) {
        onDropModeChange(false);
        return;
      }
      setDismissedQuorumProposalScope(quorumProposalScopeKey);
    };
    $[28] = canUseChatComposer;
    $[29] = fixedDropMode;
    $[30] = onDropModeChange;
    $[31] = onExitFixedDropMode;
    $[32] = quorumProposalScopeKey;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const onCloseQuorumProposal = t13;
  let t14;
  if ($[34] !== isDropMode || $[35] !== onDropModeChange) {
    t14 = () => {
      setDismissedQuorumProposalScope(null);
      if (!isDropMode) {
        onDropModeChange(true);
      }
    };
    $[34] = isDropMode;
    $[35] = onDropModeChange;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const onOpenQuorumProposal = t14;
  let t15;
  if ($[37] !== canMentionAll) {
    t15 = partIndex => {
      setDrop(prevDrop => {
        if (!prevDrop) {
          return null;
        }
        const newParts = prevDrop.parts.filter((_, i) => i !== partIndex);
        return {
          ...prevDrop,
          parts: newParts,
          referenced_nfts: prevDrop.referenced_nfts,
          mentioned_users: prevDrop.mentioned_users,
          mentioned_groups: getMentionedGroupsFromParts(newParts, canMentionAll),
          mentioned_waves: prevDrop.mentioned_waves ?? [],
          metadata: prevDrop.metadata
        };
      });
    };
    $[37] = canMentionAll;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const onRemovePart = t15;
  let t16;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = new Map();
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const slowModeChatStateByWaveRef = useRef(t16);
  const slowModeChatReservationIdRef = useRef(0);
  let t17;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = waveId => {
      const currentState = slowModeChatStateByWaveRef.current.get(waveId);
      if (currentState !== undefined) {
        return currentState;
      }
      const nextState = {
        pendingReservationId: null,
        cooldownUntil: null,
        cooldownMs: null
      };
      slowModeChatStateByWaveRef.current.set(waveId, nextState);
      return nextState;
    };
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const getSlowModeChatWaveState = t17;
  let t18;
  if ($[41] !== connectedProfile?.handle || $[42] !== wave.author.handle || $[43] !== wave.chat.slow_mode_cooldown_ms || $[44] !== wave.id || $[45] !== wave.wave.authenticated_user_eligible_for_admin) {
    t18 = dropRequest => {
      if (dropRequest.wave_id !== wave.id) {
        return null;
      }
      const cooldownMs = wave.chat.slow_mode_cooldown_ms;
      const connectedHandle = connectedProfile?.handle?.toLowerCase() ?? null;
      const isCreator = connectedHandle !== null && wave.author.handle?.toLowerCase() === connectedHandle;
      const isAdmin = wave.wave.authenticated_user_eligible_for_admin === true;
      if (typeof cooldownMs !== "number") {
        return null;
      }
      if (dropRequest.drop_type !== ApiDropType.Chat || connectedHandle === null || isCreator || isAdmin) {
        return null;
      }
      return cooldownMs;
    };
    $[41] = connectedProfile?.handle;
    $[42] = wave.author.handle;
    $[43] = wave.chat.slow_mode_cooldown_ms;
    $[44] = wave.id;
    $[45] = wave.wave.authenticated_user_eligible_for_admin;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  connectedProfile?.handle;
  const getLocalSlowModeCooldownMs = t18;
  let t19;
  if ($[47] !== getLocalSlowModeCooldownMs) {
    t19 = dropRequest_0 => {
      const cooldownMs_0 = getLocalSlowModeCooldownMs(dropRequest_0);
      if (cooldownMs_0 === null) {
        return null;
      }
      const waveState = getSlowModeChatWaveState(dropRequest_0.wave_id);
      if (waveState.pendingReservationId !== null) {
        return false;
      }
      const cooldownUntil = waveState.cooldownUntil;
      if (cooldownUntil !== null && Date.now() < cooldownUntil) {
        return false;
      }
      waveState.cooldownUntil = null;
      waveState.cooldownMs = cooldownMs_0;
      slowModeChatReservationIdRef.current = slowModeChatReservationIdRef.current + 1;
      const reservation = {
        id: slowModeChatReservationIdRef.current,
        waveId: dropRequest_0.wave_id,
        cooldownMs: cooldownMs_0
      };
      waveState.pendingReservationId = reservation.id;
      return reservation;
    };
    $[47] = getLocalSlowModeCooldownMs;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  const reserveSlowModeChatQueueSlot = t19;
  let t20;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = reservation_0 => {
      if (reservation_0 === null || reservation_0 === undefined) {
        return;
      }
      const waveState_0 = slowModeChatStateByWaveRef.current.get(reservation_0.waveId);
      if (waveState_0 === undefined) {
        return;
      }
      if (waveState_0.pendingReservationId !== reservation_0.id) {
        return;
      }
      waveState_0.pendingReservationId = null;
      waveState_0.cooldownMs = null;
    };
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  const clearSlowModeChatPending = t20;
  let t21;
  if ($[50] !== queryClient || $[51] !== wave) {
    t21 = body => {
      const reservation_1 = body.slowModeChatReservation;
      if (reservation_1 === undefined) {
        return;
      }
      const waveState_1 = slowModeChatStateByWaveRef.current.get(reservation_1.waveId);
      if (waveState_1?.pendingReservationId !== reservation_1.id) {
        return;
      }
      const nextDropAllowed = Date.now() + reservation_1.cooldownMs;
      waveState_1.pendingReservationId = null;
      waveState_1.cooldownUntil = nextDropAllowed;
      waveState_1.cooldownMs = reservation_1.cooldownMs;
      queryClient.setQueryData([QueryKey.WAVE, {
        wave_id: reservation_1.waveId
      }], currentWave => {
        const sourceWave = currentWave ?? (wave.id === reservation_1.waveId ? wave : undefined);
        if (sourceWave === undefined) {
          return currentWave;
        }
        return {
          ...sourceWave,
          chat: {
            ...sourceWave.chat,
            next_drop_allowed: nextDropAllowed
          }
        };
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.WAVE, {
          wave_id: reservation_1.waveId
        }]
      }).catch(_temp);
    };
    $[50] = queryClient;
    $[51] = wave;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  const startLocalSlowModeCooldown = t21;
  let t22;
  if ($[53] !== curationComposerVariant || $[54] !== processDropRemoved || $[55] !== processIncomingDrop || $[56] !== setToast || $[57] !== startLocalSlowModeCooldown || $[58] !== submissionExperience) {
    t22 = (serverDrop, body_1) => {
      if (body_1.dropId) {
        processDropRemoved(body_1.drop.wave_id, body_1.dropId);
      }
      startLocalSlowModeCooldown(body_1);
      processIncomingDrop(serverDrop, ProcessIncomingDropType.DROP_INSERT);
      body_1.onSuccess?.();
      if (submissionExperience === WaveSubmissionExperience.CURATION_LEGACY && curationComposerVariant === "leaderboard") {
        setToast({
          message: "Drop submitted successfully",
          type: "success"
        });
      }
    };
    $[53] = curationComposerVariant;
    $[54] = processDropRemoved;
    $[55] = processIncomingDrop;
    $[56] = setToast;
    $[57] = startLocalSlowModeCooldown;
    $[58] = submissionExperience;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== processDropRemoved || $[61] !== setToast) {
    t23 = (error, body_2) => {
      clearSlowModeChatPending(body_2.slowModeChatReservation);
      setTimeout(() => {
        if (body_2.dropId) {
          processDropRemoved(body_2.drop.wave_id, body_2.dropId);
        }
      }, 0);
      setToast({
        message: error instanceof Error ? error.message : String(error),
        type: "error"
      });
      body_2.onError?.(error);
    };
    $[60] = processDropRemoved;
    $[61] = setToast;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== t22 || $[64] !== t23) {
    t24 = {
      mutationFn: _temp2,
      onSuccess: t22,
      onError: t23,
      retry: _temp3,
      retryDelay: _temp4
    };
    $[63] = t22;
    $[64] = t23;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  const addDropMutation = useMutation(t24);
  let t25;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = [];
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  const queueRef = useRef(t25);
  const isProcessingRef = useRef(false);
  const hasBatchErrorsRef = useRef(false);
  const inFlightProcessNextDropRef = useRef(null);
  let t26;
  if ($[67] !== addDropMutation || $[68] !== onAllDropsAdded || $[69] !== waitAndInvalidateDrops) {
    t26 = async () => {
      if (isProcessingRef.current || queueRef.current.length === 0) {
        return;
      }
      isProcessingRef.current = true;
      while (queueRef.current.length > 0) {
        const dropRequest_1 = queueRef.current.shift();
        if (dropRequest_1 === undefined) {
          break;
        }
        ;
        try {
          await addDropMutation.mutateAsync(dropRequest_1);
        } catch (t27) {
          const error_0 = t27;
          hasBatchErrorsRef.current = true;
          console.error("Error processing drop:", error_0);
        }
      }
      isProcessingRef.current = false;
      const shouldNotifyAllDropsAdded = !hasBatchErrorsRef.current;
      hasBatchErrorsRef.current = false;
      waitAndInvalidateDrops();
      if (shouldNotifyAllDropsAdded) {
        onAllDropsAdded?.();
      }
    };
    $[67] = addDropMutation;
    $[68] = onAllDropsAdded;
    $[69] = waitAndInvalidateDrops;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  const processNextDrop = t26;
  let t27;
  if ($[71] !== onDropAddedToQueue || $[72] !== processNextDrop || $[73] !== reserveSlowModeChatQueueSlot || $[74] !== unreadDividerContext) {
    t27 = dropRequest_2 => {
      const slowModeChatReservation = reserveSlowModeChatQueueSlot(dropRequest_2.drop);
      if (slowModeChatReservation === false) {
        return false;
      }
      const queuedDropRequest = slowModeChatReservation === null ? dropRequest_2 : {
        ...dropRequest_2,
        slowModeChatReservation
      };
      queueRef.current.push(queuedDropRequest);
      inFlightProcessNextDropRef.current = processNextDrop();
      if (unreadDividerContext) {
        unreadDividerContext.setUnreadDividerSerialNo(null);
      }
      onDropAddedToQueue();
      if (dropRequest_2.drop.drop_type !== ApiDropType.Chat && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      return true;
    };
    $[71] = onDropAddedToQueue;
    $[72] = processNextDrop;
    $[73] = reserveSlowModeChatQueueSlot;
    $[74] = unreadDividerContext;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  const submitDrop = t27;
  const hasExitFixedDropMode = onExitFixedDropMode !== undefined;
  const t28 = fixedDropMode === DropMode.PARTICIPATION && hasExitFixedDropMode ? "Close create drop" : null;
  const t29 = fixedDropMode === DropMode.BOTH && (privileges.chatRestriction === null || privileges.chatRestriction === ChatRestriction.SLOW_MODE) || fixedDropMode === DropMode.PARTICIPATION && hasExitFixedDropMode;
  const t30 = privileges.chatRestriction === ChatRestriction.SLOW_MODE;
  let t31;
  if ($[76] !== activeDrop || $[77] !== canUseCurationUrlSubmit || $[78] !== curationUrlRestrictionMessage || $[79] !== drop || $[80] !== dropId || $[81] !== externalAttachmentDrop || $[82] !== identityPickerPlacement || $[83] !== isDropMode || $[84] !== isStormMode || $[85] !== onCancelReplyQuote || $[86] !== onDropModeChange || $[87] !== onExternalAttachmentDropConsumed || $[88] !== onSwitchToDropModeWithUrl || $[89] !== submitDrop || $[90] !== t28 || $[91] !== t29 || $[92] !== t30 || $[93] !== termsSignatureFlowEnabled) {
    t31 = {
      activeDrop,
      onCancelReplyQuote,
      drop,
      isStormMode,
      isDropMode,
      dropId,
      setDrop,
      setIsStormMode,
      onDropModeChange,
      onSwitchToDropModeWithUrl,
      submitDrop,
      dropModeToggleExitLabel: t28,
      canExitDropMode: t29,
      isChatBlockedBySlowMode: t30,
      externalAttachmentDrop,
      onExternalAttachmentDropConsumed,
      canSubmitCurationUrl: canUseCurationUrlSubmit,
      curationUrlSubmitRestrictionMessage: curationUrlRestrictionMessage,
      termsSignatureFlowEnabled,
      identityPickerPlacement
    };
    $[76] = activeDrop;
    $[77] = canUseCurationUrlSubmit;
    $[78] = curationUrlRestrictionMessage;
    $[79] = drop;
    $[80] = dropId;
    $[81] = externalAttachmentDrop;
    $[82] = identityPickerPlacement;
    $[83] = isDropMode;
    $[84] = isStormMode;
    $[85] = onCancelReplyQuote;
    $[86] = onDropModeChange;
    $[87] = onExternalAttachmentDropConsumed;
    $[88] = onSwitchToDropModeWithUrl;
    $[89] = submitDrop;
    $[90] = t28;
    $[91] = t29;
    $[92] = t30;
    $[93] = termsSignatureFlowEnabled;
    $[94] = t31;
  } else {
    t31 = $[94];
  }
  const createDropContentProps = t31;
  let dropComposerContent;
  if (isQuorumProposalDropMode) {
    let t32;
    if ($[95] !== activeDrop || $[96] !== dropId || $[97] !== isQuorumProposalModalOpen || $[98] !== onCancelReplyQuote || $[99] !== onCloseQuorumProposal || $[100] !== submitDrop || $[101] !== termsSignatureFlowEnabled || $[102] !== wave) {
      t32 = <QuorumProposalDropModal isOpen={isQuorumProposalModalOpen} activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} wave={wave} dropId={dropId} submitDrop={submitDrop} onClose={onCloseQuorumProposal} termsSignatureFlowEnabled={termsSignatureFlowEnabled} />;
      $[95] = activeDrop;
      $[96] = dropId;
      $[97] = isQuorumProposalModalOpen;
      $[98] = onCancelReplyQuote;
      $[99] = onCloseQuorumProposal;
      $[100] = submitDrop;
      $[101] = termsSignatureFlowEnabled;
      $[102] = wave;
      $[103] = t32;
    } else {
      t32 = $[103];
    }
    let t33;
    if ($[104] !== isQuorumProposalModalOpen || $[105] !== onOpenQuorumProposal) {
      t33 = !isQuorumProposalModalOpen && <div className="tw-flex tw-w-full tw-justify-end"><button type="button" onClick={onOpenQuorumProposal} className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-iron-100 tw-transition desktop-hover:hover:tw-border-iron-500 desktop-hover:hover:tw-bg-iron-800">Create Proposal</button></div>;
      $[104] = isQuorumProposalModalOpen;
      $[105] = onOpenQuorumProposal;
      $[106] = t33;
    } else {
      t33 = $[106];
    }
    let t34;
    if ($[107] !== t32 || $[108] !== t33) {
      t34 = <>{t32}{t33}</>;
      $[107] = t32;
      $[108] = t33;
      $[109] = t34;
    } else {
      t34 = $[109];
    }
    dropComposerContent = t34;
  } else {
    if (isCurationDropMode) {
      let t32;
      if ($[110] !== activeDrop || $[111] !== curationComposerVariant || $[112] !== dropId || $[113] !== initialCurationUrl || $[114] !== isDropMode || $[115] !== onCancelReplyQuote || $[116] !== submitDrop || $[117] !== termsSignatureFlowEnabled || $[118] !== wave) {
        t32 = <CreateCurationDropContent activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} wave={wave} dropId={dropId} isDropMode={isDropMode} initialUrl={initialCurationUrl} submitDrop={submitDrop} curationComposerVariant={curationComposerVariant} termsSignatureFlowEnabled={termsSignatureFlowEnabled} />;
        $[110] = activeDrop;
        $[111] = curationComposerVariant;
        $[112] = dropId;
        $[113] = initialCurationUrl;
        $[114] = isDropMode;
        $[115] = onCancelReplyQuote;
        $[116] = submitDrop;
        $[117] = termsSignatureFlowEnabled;
        $[118] = wave;
        $[119] = t32;
      } else {
        t32 = $[119];
      }
      dropComposerContent = t32;
    } else {
      let t32;
      if ($[120] !== createDropContentProps || $[121] !== submissionExperience || $[122] !== wave) {
        t32 = <CreateDropContent {...createDropContentProps} wave={wave} submissionExperience={submissionExperience} />;
        $[120] = createDropContentProps;
        $[121] = submissionExperience;
        $[122] = wave;
        $[123] = t32;
      } else {
        t32 = $[123];
      }
      dropComposerContent = t32;
    }
  }
  let t32;
  if ($[124] !== drop?.mentioned_groups || $[125] !== drop?.mentioned_users || $[126] !== drop?.mentioned_waves || $[127] !== drop?.parts || $[128] !== drop?.referenced_nfts || $[129] !== isCurationDropMode || $[130] !== isQuorumProposalDropMode || $[131] !== isStormMode || $[132] !== onRemovePart) {
    t32 = isStormMode && !isCurationDropMode && !isQuorumProposalDropMode && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: ANIMATION_DURATION
    }}><CreateDropStormParts parts={drop?.parts ?? []} mentionedUsers={drop?.mentioned_users ?? []} mentionedGroups={drop?.mentioned_groups ?? []} mentionedWaves={drop?.mentioned_waves ?? []} referencedNfts={drop?.referenced_nfts ?? []} onRemovePart={onRemovePart} /></motion.div>;
    $[124] = drop?.mentioned_groups;
    $[125] = drop?.mentioned_users;
    $[126] = drop?.mentioned_waves;
    $[127] = drop?.parts;
    $[128] = drop?.referenced_nfts;
    $[129] = isCurationDropMode;
    $[130] = isQuorumProposalDropMode;
    $[131] = isStormMode;
    $[132] = onRemovePart;
    $[133] = t32;
  } else {
    t32 = $[133];
  }
  let t33;
  if ($[134] !== t32) {
    t33 = <AnimatePresence>{t32}</AnimatePresence>;
    $[134] = t32;
    $[135] = t33;
  } else {
    t33 = $[135];
  }
  let t34;
  if ($[136] !== dropComposerContent || $[137] !== t33) {
    t34 = <>{t33}{dropComposerContent}</>;
    $[136] = dropComposerContent;
    $[137] = t33;
    $[138] = t34;
  } else {
    t34 = $[138];
  }
  return t34;
}
function _temp4(failureCount_0) {
  return failureCount_0 * 1000;
}
function _temp3(failureCount) {
  return failureCount < 3;
}
async function _temp2(body_0) {
  return commonApiPost({
    endpoint: "drops",
    body: body_0.drop
  });
}
function _temp() {}